import React, { useState, useMemo, useEffect } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { Tabs } from 'antd';
import useFormatMessage from 'hooks/useFormatMessage';
import useDataSource from 'hooks/useDataSource';
import { AUTO_TAG } from 'shared/constants/permissions';
import { JobStatus } from 'shared/prisma/types';
import Brain from 'components/Icons/Brain';
import TagCommentsManualTab from './TagCommentsManualTab';
import TagCommentsAutoTab from './TagCommentsAutoTab';
import AutoSuggestTags from './AutoSuggestTags';

const GET_TAGS = gql`
  query($questionIds: [ID!]!) {
    themes: commentTags(questionIds: $questionIds) {
      id
      text
      type
      commentCount
    }
    sentiments: commentTags(type: SENTIMENT) {
      id
      text
      type
    }
  }
`;

const GET_QUESTIONS = gql`
  query($ids: [ID!]!) {
    questions(ids: $ids) {
      id
      title
      autoTaggings {
        id
        createdAt
        status
        input
        autoTags {
          id
        }
        createdBy {
          id
          email
        }
      }
    }
  }
`;

const GET_RESPONSES_WITH_FULL_RESPONSE = gql`
  query($surveyIds: [ID!]!, $responseIds: [ID!]!) {
    questionResponses(surveyIds: $surveyIds, responseIds: $responseIds) {
      id
      surveyResponse {
        id
        questionResponses {
          id
          question {
            id
          }
          options {
            id
          }
        }        
      }
    }
  }
`;

const GET_SURVEY_QUESTIONS = gql`
  query($surveyId: ID!) {
    survey(id: $surveyId) {
      id
      questions {
        id
        title
        group {
          id
          questions {
            id
          }
        }
        options {
          id
          title
          group {
            id
            options {
              id              
            }
          }
        }        
      }
    }
  }
`;

const TagComments = ({ question, reportComment, responses, responsesLoading, displayedSurvey, refetchResponses, surveyIds, responseIds }) => {
  const [activeTab, setActiveTab] = useState('manual');
  const [questionColumnsVisible, setQuestionColumnsVisible] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const $t = useFormatMessage();
  const { hasDataSourcePermission, aiDisabled } = useDataSource();

  const { questionIds } = question;

  const { refetch: refetchTags, loading: tagsLoading, data: { themes = [], sentiments = [] } = {} } = useQuery(GET_TAGS, {
    variables: { questionIds },
  });

  const { loading: questionsLoading, data: { survey } = {} } = useQuery(GET_SURVEY_QUESTIONS, {
    variables: { surveyId: displayedSurvey.id },
  });

  const { data: fullQuestionResponses } = useQuery(GET_RESPONSES_WITH_FULL_RESPONSE, {
    variables: { surveyIds, responseIds },
    skip: !questionColumnsVisible,
  });

  const { data: { questions } = {}, startPolling, stopPolling, refetch: refetchQuestions } = useQuery(GET_QUESTIONS, {
    variables: { ids: questionIds },
  });

  const isAutoTagging = !aiDisabled && questions?.some(q => q.autoTaggings.some(at => at.status === JobStatus.Pending || at.status === JobStatus.Running));

  useEffect(() => {
    if (isAutoTagging) {
      setIsPolling(true);
      startPolling(5000);
    } else if (isPolling) {
      stopPolling();
      refetchResponses();
    }
  }, [isPolling, isAutoTagging]);

  const canAutoTag = !aiDisabled && hasDataSourcePermission(AUTO_TAG);

  const onVisibleColumnsChange = (columns) => {
    setQuestionColumnsVisible(columns.some(c => survey?.questions.find(q => q.id === c)));
  };

  const onSuggestTagsDone = () => {
    refetchTags();
    setActiveTab('manual');
  };

  const onAutoTagDone = () => {
    refetchQuestions();
  };

  const getQuestionId = (id) => survey?.questions.find(q => q.id === id || q.group?.questions.find(gq => gq.id === id))?.id || id;
  const getOption = (id) => survey?.questions.flatMap(q => q.options).find(o => o.id === id || o.group?.options.find(go => go.id === id));

  const comments = useMemo(() => responses?.map(c => ({
    id: c.id,
    text: c.text,
    date: c.surveyResponse.respondedAt,
    themes: c.tags.filter(tag => themes.find(t => t.id === tag.id)).map(t => t.id),
    autoThemes: c.autoTag?.tags.map(t => t.id) || [],
    sentiments: c.tags.filter(tag => sentiments.find(t => t.id === tag.id)).map(t => t.id),
    autoSentiments: c.autoTag?.sentiments.map(t => t.id) || [],
    translation: c.autoTag?.translation,
    ...fullQuestionResponses?.questionResponses.find(r => r.id === c.id)?.surveyResponse.questionResponses?.reduce((acc, qr) => ({ ...acc, [getQuestionId(qr.question.id)]: qr.options?.map(o => getOption(o.id)?.title).join(', ') || qr.text }), {}),
  })) || [], [responses, themes, sentiments, survey?.questions, fullQuestionResponses]);

  const questionResponseIds = comments.map(c => c.id);

  return (
    <Tabs
      activeKey={aiDisabled ? 'manual' : activeTab}
      onChange={setActiveTab}
      items={[
        {
          key: 'manual',
          label: 'Manual',
          children: (
            <TagCommentsManualTab
              $t={$t}
              questionIds={questionIds}
              refetchTags={refetchTags}
              sentiments={sentiments}
              themes={themes}
              tagsLoading={tagsLoading}
              questionsLoading={questionsLoading}
              responsesLoading={responsesLoading}
              comments={comments}
              questionColumns={survey?.questions.filter(q => !questionIds.includes(q.id) && q.options.length > 0) || []}
              reportComment={reportComment}
              onVisibleColumnsChange={onVisibleColumnsChange}
            />
          ),
        },
        {
          key: 'ai',
          label: 'AI tagging',
          icon: <Brain />,
          disabled: !canAutoTag,
          children: (
            <TagCommentsAutoTab
              questionsLoading={questionsLoading}
              questions={questions}
              onAutoTagDone={onAutoTagDone}
              refetchQuestions={refetchQuestions}
              themes={themes}
              sentiments={sentiments}
              questionResponseIds={questionResponseIds}
              questionIds={questionIds}
            />
          ),
        },
        {
          key: 'suggest',
          label: 'AI tag suggestions',
          icon: <Brain />,
          disabled: !canAutoTag,
          children: (
            <AutoSuggestTags
              onDone={onSuggestTagsDone}
              tags={themes}
              comments={comments}
              questionResponseIds={questionResponseIds}
              questionIds={questionIds}
              questionTitle={question.title}
            />
          ),
        },
      ].filter(item => !aiDisabled || item.key === 'manual')}
    />
  );
};

export default TagComments;
